import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, useMediaQuery } from '@mui/material';

import {
  CarrierImageStyled,
  DrawerWrapperStyled,
  PlanDetailsHeaderSyled,
} from '../PlanDetailsDrawer/PlanDetailsDrawerStyled';
import {
  FamilyCoveredStyled,
  LastYearBenefitCardContentStyled,
  LogoPaperStyled,
} from './LastYearBenefitsInOeStyled';
import { selectPerPayPeriodText } from 'enrollment/services';
import { getPremiumStyled, RETURN_DISPLAY } from 'enrollment/utils';
import { DetailPillStyled } from '../PlanDetailsDrawer/components/PremiumDetails/PremiumDetailsStyled';
import { selectSubscriberDetails } from 'enrollment/features/Profile/services';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import { selectDependentDetails } from 'enrollment/features/Family/services';
import { BenefitDto } from 'theSource/queries';
import { PlanDetailsTable } from '../PlanDetailsDrawer/components';
import { DrawerDialog } from 'enrollment/components';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { usePlanInfoQuery } from 'enrollment/queries/usePlanInformationQuery';

interface LastYearBenefitPlanDetailsDrawerProps {
  queryBenefitsData: BenefitDto[];
}

export const LastYearBenefitPlanDetailsDrawer = ({
  queryBenefitsData,
}: LastYearBenefitPlanDetailsDrawerProps) => {
  const { t } = useTranslation('enrollment.lastYearBenefitInOe');

  const enrollmentContext = useSelector(selectEnrollmentContext);

  const orgPlanId = queryBenefitsData[0].coverage.req_org_plan_id;

  const { planInfoData: queryData } = usePlanInfoQuery({
    orgPlanYearId: queryBenefitsData[0].org_plan_year_id,
    enrollmentContext: enrollmentContext,
    orgPlanId: orgPlanId,
    enabled: Boolean(orgPlanId > 0 && queryBenefitsData[0].org_plan_year_id),
  });

  const drawerContentDetails = queryData?.data?.content_details;

  const isMobile = useMediaQuery('(max-width: 743px)');

  const perPayText = useSelector(selectPerPayPeriodText);

  const subscriberData = useSelector(selectSubscriberDetails);
  const dependentSelectorData = useSelector(selectDependentDetails);

  const dependentData = dependentSelectorData?.dependents?.filter(
    (dependent: any) =>
      queryBenefitsData[0]?.coverage?.req_dependents
        ?.map((reqDependent: any) => reqDependent?.dependent_id)
        ?.includes(dependent?.id)
  );

  const coverageLevelArray = queryData?.data.plan_level_costs;
  let coverageLevel =
    queryBenefitsData[0].coverage.req_coverage_level_lookup_code;

  if (coverageLevelArray) {
    coverageLevel =
      coverageLevelArray[
        queryBenefitsData[0].coverage.req_coverage_level_lookup_code
      ].level_name;
  }

  return (
    <DrawerWrapperStyled>
      {queryData?.data?.content_details &&
        queryData?.data?.content_details.length > 0 && (
          <DrawerDialog
            triggerLabel={t('common:enrollment:viewPlanDetails')}
            id={`planDetailsDrawer${orgPlanId}`}
            dialogTriggerSecondary
            dialogTriggerClass="btn-link"
            dialogLarge
            hideFooter
          >
            <div className="container-lg" style={{ margin: 0 }}>
              <div>
                <PlanDetailsHeaderSyled>
                  <h1 data-testid="plan-name">
                    {queryBenefitsData[0].coverage.req_org_plan_name}
                  </h1>
                </PlanDetailsHeaderSyled>
                <div className={!isMobile ? 'd-flex align-items-center' : ''}>
                  <LogoPaperStyled
                    data-testid="carrier-logo"
                    className={`${!isMobile ? 'mr-5' : 'mb-5'} d-flex align-items-center flex-column py-4`}
                  >
                    {queryBenefitsData[0].carrier_logo_url &&
                    queryBenefitsData[0].carrier_logo_url !== null ? (
                      <CarrierImageStyled
                        src={`${queryBenefitsData[0].carrier_logo_url}`}
                        alt={
                          queryBenefitsData[0].carrier_name ||
                          t('defaultLogoAltText')
                        }
                        data-testid="carrier-logo"
                        className="mb-4"
                      />
                    ) : null}
                    <div style={{ minHeight: '52px' }}>
                      {queryBenefitsData[0]?.coverage?.subscriber_premium &&
                        (!queryBenefitsData[0].hide_cost_if_empty ||
                          Number(
                            queryBenefitsData[0]?.coverage?.subscriber_premium
                          ) > 0) &&
                        getPremiumStyled({
                          premium: String(
                            queryBenefitsData[0]?.coverage?.subscriber_premium
                          ),
                          valueOnly: RETURN_DISPLAY,
                          perPayText: perPayText,
                        })}
                    </div>
                  </LogoPaperStyled>
                  <LastYearBenefitCardContentStyled
                    className={!isMobile ? 'ml-5' : 'pb-5'}
                  >
                    <div className={isMobile ? 'mb-4' : 'mb-5'}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600 }}
                        gutterBottom
                      >
                        {t('coverageLevelTitle')}
                      </Typography>
                      <DetailPillStyled className={isMobile ? 'px-4' : ''}>
                        {coverageLevel}
                      </DetailPillStyled>
                    </div>
                    <div>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        {t('familyCoveredTitle')}
                      </Typography>
                      <FamilyCoveredStyled className="d-flex flex-wrap">
                        <div className="d-flex align-items-center">
                          {displayDependentAvatar(
                            subscriberData?.subscriber_profile,
                            24
                          )}
                          <Typography variant="body1" className="ml-2">
                            {t('subscriberName')}
                          </Typography>
                        </div>
                        {dependentData?.map((dependent: any, index) => {
                          return (
                            <div
                              className="d-flex align-items-center"
                              key={index}
                            >
                              {displayDependentAvatar(
                                dependent?.dependent_profile,
                                24
                              )}
                              <Typography variant="body1" className="ml-2">
                                {dependent?.first_name}
                              </Typography>
                            </div>
                          );
                        })}
                      </FamilyCoveredStyled>
                    </div>
                  </LastYearBenefitCardContentStyled>
                </div>
              </div>
              {drawerContentDetails && (
                <PlanDetailsTable contentDetails={drawerContentDetails} />
              )}
            </div>
          </DrawerDialog>
        )}
    </DrawerWrapperStyled>
  );
};
